import { createFileRoute, Outlet, redirect, useRouterState } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { Menu, X, Bell, Search } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useSidebarCollapsed, useToggleSidebar } from '@/stores/uiStore';
import { usePortalStore, usePortalOpen } from '@/stores/portalStore';
import { SEARCH_PORTAL_ID } from '@/components/portals/SearchPalette';
import { NOTIF_PORTAL_ID } from '@/components/portals/NotificationPanel';
import { PortalHost } from '@/components/portals/PortalHost';
import Sidebar from '@/components/organisms/Sidebar';
import UserMenu from '@/components/organisms/UserMenu';
import Breadcrumbs from '@/components/molecules/Breadcrumbs';
import Softphone from '@/components/organisms/Softphone';
import { cn } from '@/lib/utils';
import { slideInLeft } from '@/lib/animations';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    const { isAuthenticated, checkAuth } = useAuthStore.getState();
    if (!isAuthenticated) {
      await checkAuth();
    }
    if (!useAuthStore.getState().isAuthenticated) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: AuthenticatedLayout,
});

const SIDEBAR_W_EXPANDED = 240;
const SIDEBAR_W_COLLAPSED = 64;

function AuthenticatedLayout() {
  const { t } = useTranslation();
  const collapsed = useSidebarCollapsed();
  const toggleSidebar = useToggleSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const portalStore = usePortalStore;

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // Keyboard shortcuts: ⌘[ toggle sidebar, ⌘K search, Escape closes all
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === '[' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      toggleSidebar();
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      portalStore.getState().toggle(SEARCH_PORTAL_ID);
    }
    if (e.key === 'Escape') {
      portalStore.getState().closeAll();
      if (mobileOpen) setMobileOpen(false);
    }
  }, [toggleSidebar, mobileOpen, portalStore]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  const sidebarWidth = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED;

  return (
    <div className="min-h-screen bg-background flex overflow-hidden">

      {/* ── Desktop Sidebar ─────────────────────────────────────────────── */}
      <motion.aside
        animate={{ width: sidebarWidth }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="hidden lg:flex fixed inset-y-0 left-0 z-40 flex-col bg-card border-r border-border/60 shadow-xl shadow-black/5 overflow-hidden"
        style={{ width: sidebarWidth }}
      >
        <Sidebar />
      </motion.aside>

      {/* ── Mobile Backdrop ─────────────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* ── Mobile Sidebar Drawer ───────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.aside
            key="mobile-sidebar"
            variants={slideInLeft}
            initial="initial"
            animate="animate"
            exit="exit"
            className="lg:hidden fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r border-border/60 shadow-2xl overflow-hidden"
            style={{ width: SIDEBAR_W_EXPANDED }}
            id="mobile-sidebar"
            aria-label={t('_authenticated:navigation')}
          >
            <button
              onClick={() => setMobileOpen(false)}
              aria-label={t('_authenticated:closeNavigation')}
              className="absolute top-4 right-4 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </motion.aside>
        )}
      </AnimatePresence>

      {/* ── Main Content (Desktop) ──────────────────────────────────────── */}
      <motion.div
        animate={{ marginLeft: sidebarWidth }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="hidden lg:flex flex-col flex-1 min-w-0"
        style={{ marginLeft: sidebarWidth }}
      >
        <TopBar onMobileMenuToggle={() => setMobileOpen((v) => !v)} mobileOpen={mobileOpen} isMac={isMac} />
        <main className="flex-1 overflow-auto">
          <div className="p-6 min-h-full">
            <Outlet />
          </div>
        </main>
      </motion.div>

      {/* ── Main Content (Mobile) ───────────────────────────────────────── */}
      <div className="lg:hidden flex flex-col flex-1 min-w-0">
        <TopBar onMobileMenuToggle={() => setMobileOpen((v) => !v)} mobileOpen={mobileOpen} isMac={isMac} />
        <main className="flex-1 overflow-auto">
          <div className="p-4 min-h-full">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ── Global Softphone ────────────────────────────────────────────── */}
      <Softphone />

      {/* ── All portals (search, notifications, future ones) ────────────── */}
      <PortalHost />
    </div>
  );
}

// ─── Top Bar ──────────────────────────────────────────────────────────────────

interface TopBarProps {
  onMobileMenuToggle: () => void;
  mobileOpen: boolean;
  isMac: boolean;
}

function TopBar({ onMobileMenuToggle, mobileOpen, isMac }: TopBarProps) {
  const { t } = useTranslation();
  const notifOpen = usePortalOpen(NOTIF_PORTAL_ID);

  const openSearch = useCallback(() => usePortalStore.getState().open(SEARCH_PORTAL_ID), []);
  const toggleNotif = useCallback(() => usePortalStore.getState().toggle(NOTIF_PORTAL_ID), []);

  return (
    <header className={cn(
      'sticky top-0 z-30 flex h-14 items-center gap-3 px-4 lg:px-6',
      'border-b border-border/60',
      'bg-background/80 backdrop-blur-md backdrop-saturate-150',
      'shadow-sm shadow-black/5',
    )}>
      {/* Mobile menu toggle */}
      <button
        onClick={onMobileMenuToggle}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
        aria-controls="mobile-sidebar"
        className="lg:hidden flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors shrink-0"
      >
        <AnimatePresence mode="wait" initial={false}>
          {mobileOpen
            ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}><X className="h-5 w-5" /></motion.span>
            : <motion.span key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}><Menu className="h-5 w-5" /></motion.span>
          }
        </AnimatePresence>
      </button>

      {/* Breadcrumbs */}
      <div className="flex-1 min-w-0">
        <Breadcrumbs />
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Desktop search trigger */}
        <button
          onClick={openSearch}
          aria-label={t('_authenticated:openSearch')}
          className={cn(
            'hidden md:flex items-center gap-2 h-9 rounded-lg border border-border/60 bg-muted/40 px-3 text-sm text-muted-foreground',
            'transition-all duration-200 hover:bg-muted/70 hover:border-border hover:shadow-sm cursor-pointer select-none',
          )}
        >
          <Search className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span className="text-xs">{t('_authenticated:searchAnything')}</span>
          <kbd className="ml-2 hidden lg:inline-flex h-5 items-center gap-0.5 rounded border border-border/60 bg-background px-1.5 text-[10px] font-medium text-muted-foreground">
            {isMac ? '⌘' : 'Ctrl+'}K
          </kbd>
        </button>

        {/* Mobile search button */}
        <button
          onClick={openSearch}
          aria-label={t('_authenticated:openSearch')}
          className="md:hidden flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
        >
          <Search className="h-4 w-4" />
        </button>

        {/* Notifications bell */}
        <button
          data-notif-trigger
          onClick={toggleNotif}
          aria-label={t('_authenticated:notifications')}
          aria-expanded={notifOpen}
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-colors"
        >
          <Bell className={cn('h-4 w-4 transition-colors', notifOpen && 'text-primary')} />
          <span className="absolute top-1.5 right-1.5 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
        </button>

        <UserMenu />
      </div>
    </header>
  );
}
